/**
 * Help System Query Hooks
 * React Query hooks for fetching help articles and categories
 */

import { useQuery } from '@tanstack/react-query';
import {
  getCategories,
  getArticles,
  getArticle,
  getArticlesByCategory,
  getContextualArticles,
  getRelatedArticles,
  searchArticles,
  getSuggestions,
  getPopularArticles,
  getRecentArticles,
} from '../services/helpApi';
import type { ListArticlesRequest } from '../types/help.types';

// Query keys for caching
export const helpKeys = {
  all: ['help'] as const,
  categories: () => [...helpKeys.all, 'categories'] as const,
  articles: () => [...helpKeys.all, 'articles'] as const,
  articleList: (params?: ListArticlesRequest) => [...helpKeys.articles(), 'list', params] as const,
  articlesByCategory: (category: string) => [...helpKeys.articles(), 'category', category] as const,
  articleDetail: (slug: string) => [...helpKeys.articles(), 'detail', slug] as const,
  contextual: (page: string, tier: string) => [...helpKeys.all, 'contextual', page, tier] as const,
  related: (slug: string) => [...helpKeys.all, 'related', slug] as const,
  search: (query: string) => [...helpKeys.all, 'search', query] as const,
  suggestions: (query: string) => [...helpKeys.all, 'suggestions', query] as const,
  popular: (limit: number) => [...helpKeys.all, 'popular', limit] as const,
  recent: (limit: number) => [...helpKeys.all, 'recent', limit] as const,
};

/**
 * Hook to fetch all help categories
 */
export const useHelpCategories = () => {
  return useQuery({
    queryKey: helpKeys.categories(),
    queryFn: getCategories,
    staleTime: 5 * 60 * 1000, // 5 minutes - categories don't change often
  });
};

/**
 * Hook to fetch help articles with filters and pagination
 */
export const useHelpArticles = (params?: ListArticlesRequest) => {
  return useQuery({
    queryKey: helpKeys.articleList(params),
    queryFn: () => getArticles(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch a single help article by slug
 */
export const useHelpArticle = (slug: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: helpKeys.articleDetail(slug),
    queryFn: () => getArticle(slug),
    enabled: options?.enabled ?? !!slug,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch articles by category
 */
export const useArticlesByCategory = (category: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: helpKeys.articlesByCategory(category),
    queryFn: () => getArticlesByCategory(category),
    enabled: options?.enabled ?? !!category,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch contextual help articles based on current page and user tier
 */
export const useContextualArticles = (
  page: string,
  tier: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: helpKeys.contextual(page, tier),
    queryFn: () => getContextualArticles(page, tier),
    enabled: options?.enabled ?? (!!page && !!tier),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch related articles for a given article
 */
export const useRelatedArticles = (slug: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: helpKeys.related(slug),
    queryFn: () => getRelatedArticles(slug),
    enabled: options?.enabled ?? !!slug,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to search help articles
 */
export const useArticleSearch = (query: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: helpKeys.search(query),
    queryFn: () => searchArticles(query),
    enabled: options?.enabled ?? (!!query && query.length >= 2),
    staleTime: 1 * 60 * 1000, // 1 minute - search results can be cached briefly
  });
};

/**
 * Hook to get search suggestions (autocomplete)
 */
export const useSearchSuggestions = (query: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: helpKeys.suggestions(query),
    queryFn: () => getSuggestions(query),
    enabled: options?.enabled ?? (!!query && query.length >= 2),
    staleTime: 30 * 1000, // 30 seconds - suggestions can be cached briefly
  });
};

/**
 * Hook to fetch popular help articles
 */
export const usePopularArticles = (limit: number = 10) => {
  return useQuery({
    queryKey: helpKeys.popular(limit),
    queryFn: () => getPopularArticles(limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch recently updated articles
 */
export const useRecentArticles = (limit: number = 10) => {
  return useQuery({
    queryKey: helpKeys.recent(limit),
    queryFn: () => getRecentArticles(limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Default export
export default useHelpArticles;
